<template>
  <div class="auth-card-wrapper">
    <div class="auth-card">
      <div class="auth-card-container">
        <img
          class="logo auth-logo"
          alt="Continuum Design"
          src="/continuum-design-logo.svg"
        />
        <h3 class="text-title">
          Connect {{ clientName }} to Continuum Design
        </h3>
        <p class="text-body-2 mb-2 text-medium-emphasis">
          Sign in with your Continuum Design account to allow {{ clientName }} to access your workflows on your behalf.
        </p>
      </div>

      <template v-if="missingSession">
        <div class="auth-card-container">
          <p class="message bg-error">
            This authorization link is missing or has expired. Please start the connection again from {{ clientName }}.
          </p>
        </div>
      </template>

      <template v-else-if="needsMfa">
        <div class="auth-card-container">
          <h3 class="text-title">Two-Factor Authentication</h3>
          <p class="text-body-2 mb-4">Enter the 6-digit code from your authenticator app.</p>
        </div>
        <v-form ref="mfaForm" @submit.prevent="submitMfa">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="mfa-code"
              label="Authentication Code"
              v-model="mfaCode"
              :rules="[rules.required, rules.mfaCode]"
              maxlength="6"
              autofocus
              variant="outlined"
              density="default"
              prepend-inner-icon="mdi-shield-key"
              v-on:keyup.enter="submitMfa"
            ></v-text-field>
            <p class="message bg-error" v-if="error">
              {{ error }}
            </p>
          </div>
          <div class="auth-card-container">
            <div class="auth-card-footer justify-space-between">
              <div class="auth-card-footer-part">
                <a class="text text-decoration-none" style="cursor: pointer" @click="cancelMfa">
                  Back to sign-in
                </a>
              </div>
              <div class="auth-card-footer-part">
                <v-btn
                  rounded="lg"
                  size="large"
                  color="primary"
                  class="ml-auto d-flex"
                  variant="flat"
                  :loading="submitting"
                  @click="submitMfa"
                >
                  Verify
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>
      </template>

      <template v-else>
        <v-form ref="loginForm" v-model="valid" @submit.prevent="submit">
          <div class="auth-card-container form-fields" style="margin-bottom: 8px">
            <v-text-field
              name="email"
              label="Email Address"
              type="email"
              v-model="email"
              :rules="[rules.required, rules.email]"
              autocomplete="username"
              variant="outlined"
              density="default"
              v-on:keyup.enter="submit"
            ></v-text-field>
            <v-text-field
              name="password"
              label="Password"
              v-model="password"
              :append-inner-icon="hidePassword ? 'mdi-eye-outline' : 'mdi-eye-off-outline'"
              @click:append-inner="hidePassword = !hidePassword"
              :type="hidePassword ? 'password' : 'text'"
              :rules="[rules.required]"
              autocomplete="current-password"
              variant="outlined"
              density="default"
              v-on:keyup.enter="submit"
            ></v-text-field>
            <p class="message bg-error" v-if="error">
              {{ error }}
            </p>
          </div>
          <div class="auth-card-container">
            <div class="auth-card-footer justify-end">
              <div class="auth-card-footer-part">
                <v-btn
                  rounded="lg"
                  size="large"
                  color="primary"
                  class="ml-auto d-flex"
                  variant="flat"
                  :loading="submitting"
                  @click="submit"
                >
                  Sign in
                </v-btn>
              </div>
            </div>
          </div>
        </v-form>

        <div class="auth-card-container">
          <text-divider text="or" />
          <v-btn
            class="google-cta"
            style="border-radius: 8px!important; width: 100%;"
            :disabled="submitting"
            @click="onGoogleSignIn"
          >
            <div class="google-cta__logo-container">
              <div class="google-logo-bg">
                <img src="@/assets/icon-google-svg.svg" alt="G" />
              </div>
            </div>
            <div class="google-cta__text-container">
              <span class="google-cta__text">
                Continue with Google
              </span>
            </div>
          </v-btn>
        </div>
      </template>
    </div>
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { useRoute } from 'vue-router'
import TextDivider from '@/components/TextDivider.vue'

const route = useRoute()

const MCP_API_URL = process.env.VUE_APP_MCP_URL || ''

const sessionId = ref('')
const clientName = ref('the connector')
const email = ref('')
const password = ref('')
const hidePassword = ref(true)
const valid = ref(false)
const error = ref('')
const submitting = ref(false)
const needsMfa = ref(false)
const mfaSession = ref('')
const mfaCode = ref('')

const rules = {
  required: (v) => !!v || 'This field is required',
  email: (v) => /.+@.+\..+/.test(v) || 'Enter a valid email',
  mfaCode: (v) => /^[0-9]{6}$/.test(v) || 'Enter a 6-digit code'
}

const missingSession = computed(() => !sessionId.value)

const post = async (body) => {
  if (!MCP_API_URL) {
    return { error: 'MCP server URL is not configured' }
  }

  let response
  try {
    response = await fetch(`${MCP_API_URL}/oauth/login-complete`, {
      method: 'POST',
      headers: { 'content-type': 'application/json' },
      body: JSON.stringify(body)
    })
  } catch (e) {
    return { error: `Could not reach the server: ${e.message}` }
  }

  const text = await response.text().catch(() => '')
  let data = {}
  if (text) {
    try {
      data = JSON.parse(text)
    } catch {
      return { error: `Server returned an invalid response (status ${response.status})` }
    }
  }

  if (!response.ok) {
    return { error: data.error_description || data.error || `Request failed with status ${response.status}` }
  }

  return { data }
}

const submit = async () => {
  if (submitting.value) {
    return
  }

  error.value = ''
  if (!email.value || !password.value) {
    error.value = 'Email and password are required'

    return
  }

  submitting.value = true
  try {
    const { data, error: requestError } = await post({
      session: sessionId.value,
      email: email.value,
      password: password.value
    })

    if (requestError) {
      error.value = requestError

      return
    }

    if (data.needsMfa) {
      needsMfa.value = true
      mfaSession.value = data.mfaSession

      return
    }

    if (data.redirectTo) {
      window.location.assign(data.redirectTo)
    }
  } finally {
    submitting.value = false
  }
}

const submitMfa = async () => {
  if (submitting.value) {
    return
  }

  error.value = ''
  if (!/^[0-9]{6}$/.test(mfaCode.value)) {
    error.value = 'Enter a valid 6-digit code'

    return
  }

  submitting.value = true
  try {
    const { data, error: requestError } = await post({
      session: sessionId.value,
      email: email.value,
      mfaCode: mfaCode.value,
      mfaSession: mfaSession.value
    })

    if (requestError) {
      error.value = requestError

      return
    }

    if (data.redirectTo) {
      window.location.assign(data.redirectTo)
    }
  } finally {
    submitting.value = false
  }
}

const cancelMfa = () => {
  needsMfa.value = false
  mfaSession.value = ''
  mfaCode.value = ''
  error.value = ''
}

const onGoogleSignIn = () => {
  if (!sessionId.value) {
    error.value = 'Authorization session is missing. Please start the connection again.'

    return
  }

  if (!MCP_API_URL) {
    error.value = 'MCP server URL is not configured.'

    return
  }

  const target = new URL(`${MCP_API_URL}/oauth/social-start`)
  target.searchParams.set('session', sessionId.value)
  target.searchParams.set('provider', 'google')
  window.location.assign(target.toString())
}

onMounted(() => {
  sessionId.value = (route.query.session || '').toString()
  if (route.query.client_name) {
    clientName.value = route.query.client_name.toString()
  }

  if (sessionId.value && window.history?.replaceState) {
    const url = new URL(window.location.href)
    url.searchParams.delete('session')
    window.history.replaceState({}, document.title, url.toString())
  }
})
</script>

<style scoped>
.text {
  color: var(--text-dark);
  font-size: 13px;
  text-align: left;
  margin-bottom: 0;
  line-height: 14.3px;
}

.text-decoration-none {
  text-decoration: none;
}

.logo {
  width: 110px;
  margin-top: 9px;
  margin-bottom: 30px;
}

.form-fields {
  gap: 12px;
  display: flex;
  flex-direction: column;
}

.message {
  padding: 16px;
  border-radius: 8px;
  margin-bottom: 16px;
}
</style>
